import { isParamTypeName, isParamTypeValue, ParamTypeName, ParamTypeValue, ParamValue } from "@bonusmap/api-types";
import { BaseWebComponent } from "../base-components/base-web-component.js";
import { PARAM_TYPE_MAP, PARAM_TYPE_VALUE_MAP } from "../../utils/lists.js";
interface ParameterTypeValue {
    typeName: ParamTypeName;
    typeValue: ParamTypeValue;
}
/**
 * ParameterTypeComponent - Web component for managing parameter type_name and type_value selection with interdependency validation
 * 
 * Features:
 * - Two-select system for type_name and type_value
 * - Compatibility validation to prevent invalid combinations
 * - Custom events for value changes
 * - Error state management
 * - Role-based event handling
 */
export class ParameterTypeComponent extends BaseWebComponent {
    static webComponentName: string = "parameter-type";
    static observedAttributes = ['type-name', 'type-value', 'required'];

    protected _templateId: string = "form-parameters-form-type";

    private typeNameSelect!: HTMLSelectElement;
    private typeValueSelect!: HTMLSelectElement;
    private resetSelectsButton!: HTMLButtonElement;
    private _typeName!: ParamTypeName;
    private _typeValue!: ParamTypeValue;
    private _hasError: boolean = false;
    private _errorMessage: string = "";

    constructor() {
        super();
    }

    // =============================
    // Value Properties
    // =============================
    get value(): ParameterTypeValue | null {
        const result: ParameterTypeValue | null = (!this.typeName || !this.typeValue) ? null : {
            typeName: this.typeName,
            typeValue: this.typeValue
        };
        return result;
    }
    get typeName(): ParamTypeName {
        return this._typeName;
    }

    set typeName(value: ParamTypeName) {
        // Validate type_name
        if (isParamTypeName(value) && this._typeName !== value) {
            this._typeName = value;
            this.validateCompatibility();
            this.updateSelectValues();
            this.dispatch('type-name-changed', { typeName: value, typeValue: this._typeValue });
        }
    }

    get typeValue(): ParamTypeValue {
        return this._typeValue;
    }

    set typeValue(value: ParamTypeValue) {
        if (isParamTypeValue(value) && this._typeValue !== value) {
            this._typeValue = value;
            this.validateCompatibility();
            this.updateSelectValues();
            this.dispatch('type-value-changed', { typeName: this._typeName, typeValue: value });
        }
    }

    get hasError(): boolean {
        return this._hasError;
    }

    get errorMessage(): string {
        return this._errorMessage;
    }

    get required(): boolean {
        return this.hasAttribute('required');
    }

    // =============================
    // Compatibility Maps & Logic
    // =============================

    get paramTypeMap(): Record<string, string> {
        return PARAM_TYPE_MAP;
    }

    get paramTypeValueMap(): Record<string, string> {
        return PARAM_TYPE_VALUE_MAP;
    }

    /**
     * Check if a combination of type_name and type_value is valid
     */
    private isValidCombination(typeName: string, typeValue: string): boolean {
        if (!typeName || !typeValue) {
            return true; // Allow empty selections during editing
        }

        // Boolean type can only be used with 'bool' operation
        if (typeValue === 'boolean' && typeName !== 'bool') {
            return false;
        }

        // Bool operation can only be used with boolean type
        if (typeName === 'bool' && typeValue !== 'boolean') {
            return false;
        }

        // Range types can only be used with range operations
        if ((typeValue === 'range_number' || typeValue === 'range_date') &&
            !(typeName === 'range' || typeName === 'rangeeq')) {
            return false;
        }

        // Range operations can only be used with range types (SQL compatibility)
        if ((typeName === 'range' || typeName === 'rangeeq') &&
            !(typeValue === 'range_number' || typeValue === 'range_date')) {
            return false;
        }

        // Array types can only be used with 'in' operation
        if ((typeValue === 'number_array' || typeValue === 'date_array') &&
            typeName !== 'in') {
            return false;
        }

        // 'in' operation can only be used with array types
        if (typeName === 'in' &&
            !(typeValue === 'number_array' || typeValue === 'date_array')) {
            return false;
        }

        return true;
    }

    /**
     * Get error message for invalid combinations
     */
    private getCompatibilityErrorMessage(typeName: string, typeValue: string): string {
        if (typeValue === 'boolean' && typeName !== 'bool') {
            return 'Il tipo "Sì/No" può essere utilizzato solo con operazione "Sì/No"';
        }
        if (typeName === 'bool' && typeValue !== 'boolean') {
            return 'L\'operazione "Sì/No" può essere utilizzata solo con tipo "Sì/No"';
        }
        if ((typeValue === 'range_number' || typeValue === 'range_date') &&
            !(typeName === 'range' || typeName === 'rangeeq')) {
            return 'I tipi intervallo possono essere utilizzati solo con operazioni intervallo';
        }
        if ((typeName === 'range' || typeName === 'rangeeq') &&
            !(typeValue === 'range_number' || typeValue === 'range_date')) {
            return 'Le operazioni intervallo richiedono solo tipi intervallo (range_number o range_date)';
        }
        if ((typeValue === 'number_array' || typeValue === 'date_array') &&
            typeName !== 'in') {
            return 'I tipi lista possono essere utilizzati solo con operazione "Incluso in"';
        }
        if (typeName === 'in' &&
            !(typeValue === 'number_array' || typeValue === 'date_array')) {
            return 'L\'operazione "Incluso in" richiede tipi lista';
        }
        return 'Combinazione non valida';
    }

    /**
     * Validate current selection compatibility
     */
    private validateCompatibility(): void {
        const isValid = this.isValidCombination(this._typeName, this._typeValue);

        if (isValid) {
            this.clearError();
        } else {
            this.setError(this.getCompatibilityErrorMessage(this._typeName, this._typeValue));
        }

        // Update select options based on current selection
        this.updateSelectOptions();
    }

    /**
     * Update available options based on current selection
     */
    private updateSelectOptions(): void {
        if (!this.typeNameSelect || !this.typeValueSelect) return;

        // Reset all options to enabled first
        this.resetSelectOptions();

        // Filter options based on current selection
        if (this._typeName) {
            this.filterTypeValueOptions(this._typeName);
        }
        if (this._typeValue) {
            this.filterTypeNameOptions(this._typeValue);
        }
    }

    /**
     * Reset all select options to enabled state
     */
    private resetSelectOptions(): void {
        Array.from(this.typeNameSelect.options).forEach(option => {
            if (option.value) option.disabled = false;
        });
        Array.from(this.typeValueSelect.options).forEach(option => {
            if (option.value) option.disabled = false;
        });
    }

    /**
     * Filter type_value options based on selected type_name
     */
    private filterTypeValueOptions(typeName: string): void {
        Array.from(this.typeValueSelect.options).forEach(option => {
            if (option.value && !this.isValidCombination(typeName, option.value)) {
                option.disabled = true;
            }
        });
    }

    /**
     * Filter type_name options based on selected type_value
     */
    private filterTypeNameOptions(typeValue: string): void {
        Array.from(this.typeNameSelect.options).forEach(option => {
            if (option.value && !this.isValidCombination(option.value, typeValue)) {
                option.disabled = true;
            }
        });
    }

    // =============================
    // Error Management
    // =============================

    setError(message: string): void {
        this._hasError = true;
        this._errorMessage = message;
        this.updateErrorDisplay();
    }

    clearError(): void {
        this._hasError = false;
        this._errorMessage = "";
        this.updateErrorDisplay();
    }

    private updateErrorDisplay(): void {
        if (!this.typeNameSelect || !this.typeValueSelect) return;

        const isInvalidClass = 'is-invalid';

        if (this._hasError) {
            this.typeNameSelect.classList.add(isInvalidClass);
            this.typeValueSelect.classList.add(isInvalidClass);
        } else {
            this.typeNameSelect.classList.remove(isInvalidClass);
            this.typeValueSelect.classList.remove(isInvalidClass);
        }

        // Update error message display
        const errorDiv = this.querySelector('.invalid-feedback');
        if (errorDiv) {
            errorDiv.textContent = this._errorMessage;
            errorDiv.classList.toggle('d-block', this._hasError);
        }
    }

    /**
     * Update select element values
     */
    private updateSelectValues(): void {
        if (this.typeNameSelect && this.typeNameSelect.value !== this._typeName) {
            this.typeNameSelect.value = this._typeName;
        }
        if (this.typeValueSelect && this.typeValueSelect.value !== this._typeValue) {
            this.typeValueSelect.value = this._typeValue;
        }
        this.updatePreviewText();
    }

    /**
     * Update preview text based on current selection
     */
    private updatePreviewText(): void {
        const previewElement = this.roleElementSelector('preview-text');
        if (!previewElement) return;

        // Case 1: Incomplete selection
        if (!this._typeName || !this._typeValue) {
            previewElement.textContent = 'Seleziona i valori per vedere l\'anteprima';
            previewElement.parentElement?.classList.remove('text-danger', 'text-success');
            previewElement.parentElement?.classList.add('text-info');
            return;
        }

        // Case 2: Invalid combination
        if (!this.isValidCombination(this._typeName, this._typeValue)) {
            previewElement.textContent = this.getCompatibilityErrorMessage(this._typeName, this._typeValue);
            previewElement.parentElement?.classList.remove('text-info', 'text-success');
            previewElement.parentElement?.classList.add('text-danger');
            return;
        }

        // Case 3: Valid combination - show concrete example
        const exampleText = this.getExampleText(this._typeName, this._typeValue);
        previewElement.textContent = exampleText;
        previewElement.parentElement?.classList.remove('text-info', 'text-danger');
        previewElement.parentElement?.classList.add('text-success');
    }

    /**
     * Get concrete example text for valid combinations
     */
    private getExampleText(typeName: ParamTypeName, typeValue: ParamTypeValue): string {
        const combinations: Record<string, Record<string, string>> = {
            'eq': {
                'number': 'Es. valore uguale a 100',
                'date': 'Es. data uguale al 15/03/2024',
                'boolean': 'Es. valore Sì/No'
            },
            'min': {
                'number': 'Es. valore maggiore di 50',
                'date': 'Es. data successiva al 15/03/2024'
            },
            'mineq': {
                'number': 'Es. valore maggiore o uguale a 50',
                'date': 'Es. data dal 15/03/2024 in poi'
            },
            'max': {
                'number': 'Es. valore minore di 100',
                'date': 'Es. data precedente al 31/12/2024'
            },
            'maxeq': {
                'number': 'Es. valore minore o uguale a 100',
                'date': 'Es. data fino al 31/12/2024'
            },
            'bool': {
                'boolean': 'Es. Sì oppure No'
            },
            'range': {
                'range_number': 'Es. valore compreso tra X e Y (esclusi)',
                'range_date': 'Es. data compresa tra GG/MM/AAAA e GG/MM/AAAA (escluse)'
            },
            'rangeeq': {
                'range_number': 'Es. valore compreso tra X e Y (inclusi)',
                'range_date': 'Es. data compresa tra GG/MM/AAAA e GG/MM/AAAA (incluse)'
            },
            'in': {
                'number_array': 'Es. valore presente nella lista [10, 20, 30, 50]',
                'date_array': 'Es. data presente nella lista [01/01/2024, 15/03/2024, 31/12/2024]'
            }
        };

        return combinations[typeName]?.[typeValue] || 'Es. configurazione personalizzata';
    }

    /**
     * Reset both select values to empty and clear any errors
     */
    private resetSelectValues(): void {
        // Reset internal values first
        this._typeName = '' as ParamTypeName;
        this._typeValue = '' as ParamTypeValue;
        
        // Reset select elements
        if (this.typeNameSelect) {
            this.typeNameSelect.value = '';
        }
        if (this.typeValueSelect) {
            this.typeValueSelect.value = '';
        }
        
        // Clear errors and update preview
        this.clearError();
        this.updatePreviewText();
        
        // Re-enable all options that were disabled due to compatibility
        this.updateSelectOptions();
        
        // Dispatch reset event
        this.dispatch('value-reset', { typeName: '', typeValue: '' });
    }

    // =============================
    // Event Handlers
    // =============================

    private handleTypeNameChange = (event: Event): void => {
        const target = event.target as HTMLSelectElement;
        this.typeName = target.value as ParamTypeName;
    };

    private handleTypeValueChange = (event: Event): void => {
        const target = event.target as HTMLSelectElement;
        this.typeValue = target.value as ParamTypeValue;
    };

    // =============================
    // Rendering & Lifecycle
    // =============================

    protected buildRenderingData(): Record<string, any> {
        const data = super.buildRenderingData();

        return {
            ...data,
            controlsId: this.createRandomId(),
            paramTypes: this.paramTypeMap,
            paramTypeValues: this.paramTypeValueMap,
            selectedTypeName: this._typeName,
            selectedTypeValue: this._typeValue,
            hasError: this._hasError,
            errorMessage: this._errorMessage,
            label: this.attributeStringOption('label', 'Tipo Parametro'),
            helpText: this.attributeStringOption('help-text'),
            helpLink: this.attributeStringOption('help-link'),
            disabled: this.attributeBoolOption('disabled'),
            readonly: this.attributeBoolOption('readonly'),
            required: this.required,
            typeName: this.attributeStringOption('type-name-field', 'type_name'),
            typeValue: this.attributeStringOption('type-value-field', 'type_value')
        };
    }

    protected afterRender(): void {
        super.afterRender();

        // Get select elements
        this.typeNameSelect = this.roleElementSelector('type-name-select') as HTMLSelectElement;
        this.typeValueSelect = this.roleElementSelector('type-value-select') as HTMLSelectElement;
        this.resetSelectsButton = this.roleElementSelector('reset-selects-btn') as HTMLButtonElement;
        if (!this.typeNameSelect || !this.typeValueSelect) {
            console.error('Required select elements not found');
            return;
        }
        // Set up event handlers using Custom Role Namespaced pattern
        this.setElementRoleHandler('type-name-select', this.handleTypeNameChange, 'change');
        this.setElementRoleHandler('type-value-select', this.handleTypeValueChange, 'change');
        this.setElementRoleHandler('reset-selects-btn', this.resetSelectValues.bind(this), 'click');
        // Initialize values from attributes
        const initialTypeName = this.attributeStringOption('type-name');
        const initialTypeValue = this.attributeStringOption('type-value');

        if (initialTypeName) {
            if (isParamTypeName(initialTypeName)) {
                this._typeName = initialTypeName;
            }
        }
        if (initialTypeValue) {
            if (isParamTypeValue(initialTypeValue)) {
                this._typeValue = initialTypeValue;
            }
        }

        // Update select values and validate
        this.updateSelectValues();
        this.validateCompatibility();
    }

    protected onAttributeChange(name: string, oldValue: string, newValue: string): void {
        if (name === 'type-name' && isParamTypeName(newValue)) {
            this.typeName = newValue as ParamTypeName;
        } else if (name === 'type-value' && isParamTypeValue(newValue)) {
            this.typeValue = newValue as ParamTypeValue;
        }
    }
}